import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet-async";
import "./ContactPage.css";
import { contactFormSubmit } from "../services/api";

gsap.registerPlugin(ScrollTrigger);

const faqs = [
  {
    q: "How long does the whole process take?",
    a: "Usually 4–8 months from first consultation to visa in hand, depending on your target country and whether you're aiming for the January or September intake. We'll build a realistic timeline with you on day one.",
  },
  {
    q: "Is the first consultation really free?",
    a: "Yes, completely. We evaluate your profile, walk you through the options that actually fit, and you leave with a clear picture of what's possible. No commitment, no sales pitch — just an honest conversation.",
  },
  {
    q: "Which countries do you work with?",
    a: "USA, UK, Canada, Australia, Germany, Ireland, New Zealand, and Singapore. We know the university systems, admission cycles, and visa processes inside out for each of them.",
  },
  {
    q: "I've already been rejected once. Can you still help?",
    a: "Absolutely. We've turned plenty of rejections into acceptances. We dig into what went wrong — SOP, financial documents, visa interview — and rebuild the application from a stronger foundation.",
  },
  {
    q: "What IELTS or TOEFL score do I actually need?",
    a: "Depends on the country and university, but most ask for IELTS 6.0–7.0 or TOEFL 80–100. We match your target list to realistic score goals and plan your prep timeline around it.",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const rootRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    contactFormSubmit(form);
    console.log("Contact form submitted:", form);
    setSubmitted(true);
  };

  // ── GSAP animations ──
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const ctx = gsap.context(() => {
      if (prefersReduced) {
        // Show everything immediately for reduced-motion users
        gsap.set(".cp-reveal", { opacity: 1, y: 0, x: 0, clearProps: "all" });
        return;
      }

      // ── Hero load sequence ──
      const heroTl = gsap.timeline({ defaults: { ease: "power3.out" } });
      heroTl
        .from(".cp-hero-decoration", {
          scale: 0.6,
          opacity: 0,
          duration: 1.6,
          ease: "power2.out",
        })
        .from(".cp-eyebrow", { y: -14, autoAlpha: 0, duration: 0.7 }, 0.1)
        .from(".cp-hero-title", {
          y: 40,
          autoAlpha: 0,
          duration: 1.0,
        }, "-=0.3")
        .from(".cp-hero-title em", {
          color: "#1A1A1A",
          duration: 0.6,
          ease: "power2.inOut",
        }, "-=0.3")
        .from(".cp-hero-sub", { y: 20, autoAlpha: 0, duration: 0.8 }, "-=0.6");

      // Subtle parallax on hero decoration
      gsap.to(".cp-hero-decoration", {
        y: 120,
        ease: "none",
        scrollTrigger: {
          trigger: ".cp-hero",
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      // ── Info column ──
      gsap.from(".cp-info-heading, .cp-info-lead", {
        scrollTrigger: { trigger: ".cp-info", start: "top 80%" },
        y: 28, autoAlpha: 0, duration: 0.8, stagger: 0.12, ease: "power3.out",
      });

      gsap.from(".cp-info-block", {
        scrollTrigger: { trigger: ".cp-info-blocks", start: "top 85%" },
        x: -24, autoAlpha: 0, duration: 0.7, stagger: 0.12, ease: "power3.out",
      });

      gsap.from(".cp-social-pill", {
        scrollTrigger: { trigger: ".cp-social", start: "top 92%" },
        y: 16, autoAlpha: 0, scale: 0.95,
        duration: 0.5, stagger: 0.08, ease: "back.out(1.6)",
      });

      // ── Form ──
      gsap.from(".cp-form-wrap", {
        scrollTrigger: { trigger: ".cp-form-wrap", start: "top 85%" },
        y: 40, autoAlpha: 0, duration: 0.9, ease: "power3.out",
      });

      gsap.from(".cp-form .cp-field, .cp-form .cp-form-row, .cp-form-header", {
        scrollTrigger: { trigger: ".cp-form-wrap", start: "top 75%" },
        y: 18, autoAlpha: 0, duration: 0.55, stagger: 0.07, ease: "power2.out",
      });

      // ── Map ──
      gsap.from(".cp-map-header > *", {
        scrollTrigger: { trigger: ".cp-map-header", start: "top 85%" },
        y: 24, autoAlpha: 0, duration: 0.7, stagger: 0.1, ease: "power3.out",
      });

      gsap.from(".cp-map-embed", {
        scrollTrigger: { trigger: ".cp-map-embed", start: "top 85%" },
        clipPath: "inset(0 0 100% 0)",
        duration: 1.1, ease: "power3.out",
      });

      // ── FAQ ──
      gsap.from(".cp-faq .cp-section-title, .cp-faq .cp-section-sub", {
        scrollTrigger: { trigger: ".cp-faq", start: "top 80%" },
        y: 26, autoAlpha: 0, duration: 0.7, stagger: 0.1, ease: "power3.out",
      });

      gsap.from(".cp-faq-item", {
        scrollTrigger: { trigger: ".cp-faq-list", start: "top 85%" },
        y: 20, autoAlpha: 0, duration: 0.55, stagger: 0.08, ease: "power3.out",
      });

      // ── WhatsApp float — gentle hover bob ──
      gsap.to(".whatsapp-float", {
        y: -5,
        duration: 1.8,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="contact-page" ref={rootRef}>
      <Helmet>
        <title>Contact ISTP Overseas | Study Abroad Consultants Vijayawada</title>
        <meta name="description" content="Get in touch with ISTP Overseas in Vijayawada for a free counselling session on studying abroad. Call, WhatsApp or visit our office." />
        <link rel="canonical" href="https://www.istpoverseas.com/contact" />
        <meta property="og:title" content="Contact Us | ISTP Overseas" />
        <meta property="og:description" content="Reach out to ISTP Overseas for expert study abroad guidance." />
        <meta property="og:url" content="https://www.istpoverseas.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="cp-hero">
        <div className="cp-hero-decoration" aria-hidden="true" />
        <div className="cp-hero-inner">
          <span className="cp-eyebrow">Start the conversation</span>
          <h1 className="cp-hero-title">
            Let's talk about<br /><em>your next chapter.</em>
          </h1>
          <p className="cp-hero-sub">
            A free profile evaluation, a quick visa question, or just some honest
            clarity on where to begin — we're here for it. No pressure. No sales pitch.
          </p>
        </div>
      </section>

      {/* Main Contact Grid */}
      <section className="cp-main">
        <div className="cp-grid">

          {/* Left: Info */}
          <div className="cp-info">
            <h2 className="cp-info-heading">Drop by. Or drop a line.</h2>
            <p className="cp-info-lead">
              Our Vijayawada office is open Monday through Saturday. Walk in without
              an appointment, book a slot online, or just WhatsApp us — whichever's easiest.
            </p>

            <div className="cp-info-blocks">

              <div className="cp-info-block">
                <div className="cp-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <h4>Our Office</h4>
                  <p>1st Floor, KP Street, Thota's Vivedha Apartment,<br />Bunder Rd, near PVP Mall,<br />Vijayawada, Andhra Pradesh — 520010</p>
                </div>
              </div>

              <div className="cp-info-block">
                <div className="cp-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.4 2 2 0 0 1 3 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <h4>Call or WhatsApp</h4>
                  <a href="tel:+919110714332">+91 91107 14332</a>
                  <br />
                  <a href="https://wa.me/919110714332" target="_blank" rel="noopener noreferrer" className="cp-wa-link">
                    Message on WhatsApp →
                  </a>
                </div>
              </div>

              <div className="cp-info-block">
                <div className="cp-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                    <polyline points="3,9 12,14 21,9" />
                  </svg>
                </div>
                <div>
                  <h4>Email Us</h4>
                  <a href="mailto:info@istpoverseas.com">info@istpoverseas.com</a>
                  <p style={{ marginTop: 4, fontSize: 13 }}>We reply within a few hours.</p>
                </div>
              </div>

              <div className="cp-info-block">
                <div className="cp-info-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div>
                  <h4>When We're Open</h4>
                  <p>Monday – Saturday · 9:00 AM – 7:00 PM<br />Closed on Sundays</p>
                </div>
              </div>

            </div>

            <div className="cp-social">
              <h4>Follow along</h4>
              <div className="cp-social-links">
                <a href="https://www.instagram.com/istpoverseaseducation/" target="_blank" rel="noopener noreferrer" className="cp-social-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                  Instagram
                </a>
                <a href="https://maps.app.goo.gl/hbTNY9V66giUicr19" target="_blank" rel="noopener noreferrer" className="cp-social-pill">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Google Maps
                </a>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="cp-form-wrap">
            {submitted ? (
              <div className="cp-success">
                <div className="cp-success-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C0392B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3>Your message is on its way.</h3>
                <p>Thanks for reaching out. Someone from the team will get back to you within 24 hours — often sooner.</p>
                <button className="btn-primary" onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", service: "", message: "" }); }}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <form className="cp-form" onSubmit={handleSubmit}>
                <div className="cp-form-header">
                  <h2>Tell us about you.</h2>
                  <p>A few details is all we need. Average response on working days: under 3 hours.</p>
                </div>

                <div className="cp-form-row">
                  <div className="cp-field">
                    <label htmlFor="name">Full Name *</label>
                    <input type="text" id="name" name="name" placeholder="Your full name" value={form.name} onChange={handleChange} required />
                  </div>
                  <div className="cp-field">
                    <label htmlFor="phone">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={handleChange} required />
                  </div>
                </div>

                <div className="cp-field">
                  <label htmlFor="email">Email Address *</label>
                  <input type="email" id="email" name="email" placeholder="you@email.com" value={form.email} onChange={handleChange} required />
                </div>

                <div className="cp-field">
                  <label htmlFor="service">What are you looking for?</label>
                  <select id="service" name="service" value={form.service} onChange={handleChange}>
                    <option value="">Pick one (optional)…</option>
                    <option value="career">Career Counselling</option>
                    <option value="university">University Selection</option>
                    <option value="application">Application Assistance</option>
                    <option value="visa">Visa Assistance</option>
                    <option value="testprep">Test Prep (IELTS / GRE / GMAT)</option>
                    <option value="departure">Pre-Departure Guidance</option>
                    <option value="general">General Enquiry</option>
                  </select>
                </div>

                <div className="cp-field">
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" placeholder="Tell us where you are right now — your degree, goals, questions, anything on your mind." rows={5} value={form.message} onChange={handleChange} />
                </div>

                <button type="submit" className="btn-primary cp-submit">
                  Send Message →
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="cp-map">
        <div className="cp-map-header">
          <h2>Find us.</h2>
          <p>A two-minute walk from PVP Mall, right on Bunder Road. Any auto driver in Vijayawada knows the spot.</p>
        </div>
        <div className="cp-map-embed">
          <iframe
            title="ISTP Office Location"
            src="https://www.google.com/maps?q=16.5010471,80.6419648&z=17&output=embed"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="cp-faq">
        <h2 className="cp-section-title">Questions we hear a lot.</h2>
        <p className="cp-section-sub">Quick answers to what's probably on your mind.</p>

        <div className="cp-faq-list">
          {faqs.map((f, i) => (
            <div
              key={i}
              className={`cp-faq-item ${openFaq === i ? "open" : ""}`}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div className="cp-faq-q">
                <span>{f.q}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cp-faq-arrow">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
              {openFaq === i && (
                <div className="cp-faq-a">
                  <p>{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* WhatsApp Float */}
      <div className="whatsapp-wrapper">
        <div className="whatsapp-tooltip">Quicker on WhatsApp?</div>
        <a href="https://wa.me/919110714332" target="_blank" rel="noopener noreferrer" className="whatsapp-float" aria-label="Chat on WhatsApp">
          <img src="https://img.icons8.com/color/480/000000/whatsapp--v1.png" alt="WhatsApp" style={{ width: "42px", height: "42px", objectFit: "contain" }} />
        </a>
      </div>
    </div>
  );
}